import { useTheme } from '@emotion/react';
import { useAtomValue } from 'jotai';
import React, { useCallback, useRef } from 'react';

import { Alert, Box, Button, Drawer, Grid, Header, Loader } from '~/components/blocks';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import { settingsDrawerState } from '~/state/settings_systemsettings/atoms';

import { PermissionError } from '../PermissionError';
import { SettingForm } from './SettingForm';
import { useDefaultValues } from './use-default_values';
import { useFetchNotificationSetting } from './use-fetch-notification_setting';
import { useUpdateNotificationSetting } from './use-update-notification_setting';

export const NotificationSettingDrawer = () => {
  const theme = useTheme();
  const formRef = useRef<RefAttributeType<typeof SettingForm>>(null);
  const state = useAtomValue(settingsDrawerState);
  const isOpen = state.drawer === 'notificationSetting';

  const { loading, isLightPlan, setting, organization } = useFetchNotificationSetting(isOpen);
  const defaultValues = useDefaultValues(
    isLightPlan,
    organization?.organizationContactPoints || [],
    setting,
  );

  const { updating, error, handleClose, handleSubmit } = useUpdateNotificationSetting(
    organization?.id,
  );

  const { verifyRole } = usePractitionerRole();
  const isEnabledEdit = verifyRole(['admin', 'company_admin']);

  const handleClick = useCallback(() => {
    if (!formRef.current) return;
    formRef.current.submitForm();
  }, []);

  return (
    <Drawer open={isOpen} onClose={handleClose}>
      <Grid height="100%" gridTemplateRows="min-content 1fr min-content">
        <Header attached="drawer">通知設定</Header>
        {loading ? (
          <Loader open inside />
        ) : (
          <>
            <Box px={theme.space.l}>
              {error && (
                <Alert status="error" mb={theme.space.l}>
                  {error}
                </Alert>
              )}
              <SettingForm
                ref={formRef}
                basicPlan={!isLightPlan}
                disabled={updating}
                defaultValues={defaultValues}
                onSubmit={handleSubmit}
              />
            </Box>
            <Drawer.Footer>
              {!isEnabledEdit && <PermissionError />}
              <Button
                wide="fill"
                use="base"
                size="l"
                loading={updating}
                disabled={!isEnabledEdit}
                onClick={handleClick}
              >
                保存
              </Button>
            </Drawer.Footer>
          </>
        )}
      </Grid>
    </Drawer>
  );
};
